import $ from '@sparta.global.vendors/jquery';
import autoCompleteUtility from '@sparta.digital-mortgage-application.utilities/auto-complete-utility/dist/es6.js';
import spartaEventsUtility from '@sparta.global.utilities/sparta-events-utility/dist/es6.js';
import animationUtility from '@sparta.digital-mortgage-application.utilities/animation-utility/dist/es6.js';

/* globals google */
const GOOGLE_API_KEY = spaParams.siteParams.googlePlacesApiKey;
const emptyAddress = {
  streetNumber: '',
  addressLineOne: '',
  city: '',
  state: '',
  zipcode: ''
};
let copy = {
  dontSeeAddressCopy: null,
  poweredByGoogleImg: null,
  poweredByGoogleAlt: null
};

/**
 * Prefills the address into a string
 * @param {Object} prefilledAddress Prefilled address object
 * @returns {String} Formatted prefilled address
 */
function prefillAddress(prefilledAddress) {
  if (!prefilledAddress) return '';
  const nameArr = ['streetNumber', 'addressLineOne', 'addressLineTwo', 'city', 'state'];
  const strBuilderArr = [];
  for (let i = 0; i < nameArr.length; i += 1) {
    const val = prefilledAddress[nameArr[i]] || '';
    if (val.length) strBuilderArr.push(val);
  }
  return strBuilderArr.join(', ').trim();
}

/**
 * Attaches the google maps API script to the DOM
 * @returns {Promise<String>} Promise resolved when script is loaded
 */
function addGoogleScript() {
  return new Promise((resolve, reject) => {
    const googleScriptSrc = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_API_KEY}&libraries=places`;
    const googleScriptExist = $(`[src="${googleScriptSrc}"]`).length === 1;
    // if the script has already been added OR if google is available then skip
    if (googleScriptExist || window.google) {
      resolve('loaded');
    } else {
      // otherwise add the google api script to the body
      // it is then available to all modules in the entire app
      const body = document.getElementsByTagName('body')[0];
      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.src = googleScriptSrc;
      body.appendChild(script);
      script.onload = () => {
        resolve('loaded');
      };
      script.onerror = () => {
        reject('error'); // eslint-disable-line prefer-promise-reject-errors
      };
    }
  });
}

/**
 * Create simple object structure from google address description
 * @param {String} selectedAddressStr String of the selected address
 * @param {Object} place Address with address components
 * @returns {Object} Map of address components to value
 */
function addressSelected(selectedAddressStr, place) {
  const addressComponents = place.address_components;
  const idMapping = {
    street_number: 'streetNumber',
    route: 'addressLineOne',
    locality: 'city',
    sublocality: 'city',
    administrative_area_level_1: 'state',
    postal_code: 'zipcode'
  };
  const componentForm = {
    street_number: 'short_name',
    route: 'long_name',
    locality: 'long_name',
    sublocality: 'long_name',
    administrative_area_level_1: 'short_name',
    postal_code: 'short_name'
  };
  const selectedAddressMap = {};

  // Get each component of the address from the place details and fill
  // the corresponding field on the form.
  (addressComponents || []).forEach((component, i) => {
    if (i === 0 && component.types[0] !== 'street_number') {
      if (parseInt(selectedAddressStr.substring(0, 1), 10)) {
        const theNum = selectedAddressStr.replace(/\D/g, '');
        selectedAddressMap.streetNumber = theNum;
      }
    }
    (component.types || []).forEach(componentType => {
      const key = componentForm[componentType];
      if (key) {
        const val = component[key];
        const id = idMapping[componentType];
        selectedAddressMap[id] = val;
      }
    });
  });
  return selectedAddressMap;
}

/**
 * Gets the places array from prediction request.
 * @note
 * Below docs contains info on prediction requests
 * https://developers.google.com/maps/documentation/javascript/reference/places-autocomplete-service#AutocompletionRequest
 * @param {google.maps.places.AutocompletionRequest} predictionRequest Request info
 * @returns {Promise<Array<{description: String, place_id: String}>>} predictions
 */
function getPlacesArr(predictionRequest) {
  const autocompleteService = new google.maps.places.AutocompleteService();
  return new Promise((resolve, reject) => {
    // setting up flag, if we don't get result in 10 seconds then reject promise
    let isResultReceived = false;
    const resultReceivedTimer = setTimeout(() => {
      if (!isResultReceived) reject('google api timed out after 10 seconds'); // eslint-disable-line prefer-promise-reject-errors
    }, 10000);
    autocompleteService.getPlacePredictions(predictionRequest, (predictions, status) => {
      // setting it true, so it will stop rejecting promise
      isResultReceived = true;
      // clearing out timeout
      clearTimeout(resultReceivedTimer);
      if (status === 'OK') {
        if (predictions) {
          const arr = predictions.map(el => ({
            description: el.description,
            place_id: el.place_id
          }));
          resolve(arr);
        } else {
          reject(`Google API query failed with status: ${status}, and predictions: ${predictions}`); // eslint-disable-line prefer-promise-reject-errors
        }
      } else if (status === 'ZERO_RESULTS') {
        resolve([]); // sending empty array in case of zero records dropdown will come
      } else {
        reject(status);
      }
    });
  });
}

/**
 * Adds a spinner to the DOM
 * @param {String} insertWhere Id of the element to insert at without #
 * @returns {void}
 */
function addSpinner(insertWhere) {
  const element = `#${insertWhere}`;
  const $autoComplete = $(element).closest('.row');
  if ($autoComplete) {
    let $spinnerWrapper;
    if ($autoComplete.find('.spinner-wrapper').length > 0) {
      $spinnerWrapper = $autoComplete.find('.spinner-wrapper');
      $spinnerWrapper.removeClass('hide');
    } else {
      $spinnerWrapper = $('<div>').addClass('spinner-wrapper');
      const spinnerProps = {
        width: 30,
        height: 30,
        strokeWidth: 2,
        speed: 10,
        color: '#f2f2f2',
        fillColor: '#00589A'
      };
      $autoComplete.append($spinnerWrapper);
      animationUtility.drawSpinner($spinnerWrapper[0], spinnerProps);
    }
  }
}

/**
 * Get's the URL params and returns the value of the param it get's passed in paramName
 * @method getURLParam
 * @param {String} paramName URL Param name
 * @returns {String} Value in paramName
 */
function getURLParam(paramName) {
  const results = new RegExp(`[?&]${paramName}=([^&#]*)`).exec(window.location.href);
  if (results == null) {
    return null;
  }
  return decodeURI(results[1]) || 0;
}

/**
 * Looks for `address=bypass` in the url and if found, hide's the google address input field and
 * display's the full address form.
 * @method lookForGoogleAddressBypass
 * @returns {void}
 */
function lookForGoogleAddressBypass() {
  const addressFlag = getURLParam('address');
  if (addressFlag && addressFlag === 'bypass') {
    $('.auto-completed-fields').fadeIn('slow').removeClass('hidden');
    $('.auto-complete').fadeOut('slow').addClass('hide');
    $('#streetNumber').focus();
  }
}

/**
 * Initializes the google address utility.
 * @param {String} fieldId ID of the field in DOM
 * @param {Object} prefilledAddress Prefilled address info if available
 * @param {Function} callback Callback to assign input values on completion/with prefill
 * @param {Function} runFailureCB Callback when google address fails to load
 * @returns {void}
 */
function initGoogleAddress(fieldId, prefilledAddress, callback, runFailureCB) {
  const field = document.getElementById(fieldId);
  if (!field) return;
  field.parentElement.classList.add('google-container');
  lookForGoogleAddressBypass();
  addGoogleScript().then(() => {
    const strAddress = prefillAddress(prefilledAddress);
    if (strAddress.length && field) {
      field.value = strAddress;
      callback(prefilledAddress);
    }
    const options = {
      matchInput: true,
      debounceMs: 0,
      minLength: 3,
      source() {
        return new Promise((resolve, reject) => {
          const request = {
            componentRestrictions: {
              country: 'us'
            },
            types: ['geocode']
          };
          if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(position => {
              request.location = new google.maps.LatLng({
                lat: position.coords.latitude,
                lng: position.coords.longitude
              });
              request.radius = position.coords.accuracy;
            });
          }
          const predictionRequest = {
            ...request,
            input: document.getElementById(fieldId).value
          };
          const placesArrPromise = getPlacesArr(predictionRequest);
          setTimeout(addSpinner(fieldId), 1000);
          placesArrPromise.then(res => {
            resolve(res);
          }).catch(e => {
            runFailureCB();
            console.log(`error retrieving places ${e}`); // eslint-disable-line no-console
            spartaEventsUtility.pub('log-event', {
              messageId: 'google-address-find-error',
              messageText: e,
              priority: 'ERROR'
            });
            reject(`error retrieving places ${e}`); // eslint-disable-line prefer-promise-reject-errors
          }).then(() => {
            setTimeout(() => {
              $('.spinner-wrapper').addClass('hide');
            }, 500);
          });
        });
      },
      processingRows(rows) {
        const arr = rows.map(e => `<span class="place-marker-icon location-icon"></span><span data-place-id="${e.place_id}" class="place-text">${e.description}</span>`);
        arr.push(`
          <button type="button" class="dont-see-address spa-btn spa-btn--link">${copy.dontSeeAddressCopy}</button>
          <hr class="divider--horizontal show-for-small-only"/>
          <img src="${copy.poweredByGoogleImg}" class="google-icon google-powered-by-logo" alt="${copy.poweredByGoogleAlt}"/>
        `);
        return arr;
      },
      clickCallBack(e) {
        const data = e;
        let placeId = '';
        $('.spinner-wrapper').removeClass('hide');
        if (data.childNodes[0]) {
          // todo fix to be dynamic
          if (data.childNodes[0].childNodes[1]) {
            placeId = data.childNodes[0].childNodes[1].getAttribute('data-place-id');
            // remove error message since we're going to hide the input and fill in the address
            // if we have a placeid
            let selector = data;
            while (selector && selector.classList && !selector.classList.contains('overlay-wrapper')) {
              selector = selector.parentElement;
            }
            let errorMessageSel = selector;
            let labelErrorSel = selector;
            while (errorMessageSel && errorMessageSel.classList && !errorMessageSel.classList.contains('spa-input-error-message')) {
              errorMessageSel = errorMessageSel.nextElementSibling;
            }
            while (labelErrorSel && labelErrorSel.classList && !labelErrorSel.classList.contains('spa-error-form-label--validation')) {
              labelErrorSel = labelErrorSel.previousElementSibling;
            }
            if (errorMessageSel && errorMessageSel.classList && errorMessageSel.classList.contains('spa-input-error-message')) {
              errorMessageSel.classList.add('hide');
            }
            if (labelErrorSel && labelErrorSel.classList && labelErrorSel.classList.contains('spa-error-form-label--validation')) {
              labelErrorSel.classList.remove('spa-error-form-label--validation');
            }
          }
        }

        // clear it out until we get a response back
        document.getElementById(fieldId).value = '';

        // click don't see address element
        if (data.getElementsByClassName('dont-see-address').length) {
          callback(emptyAddress);
        } else if (placeId) {
          // click an actual address
          const geocoder = new google.maps.Geocoder();
          geocoder.geocode({
            placeId
          }, (results, status) => {
            if (status === 'OK') {
              const selectedAddress = addressSelected(document.getElementById(fieldId).value, results[0]);
              selectedAddress.addressLineOne = (selectedAddress.addressLineOne || '').slice(0, 45);
              callback(selectedAddress);
            }
          });
        }
      },
      adaOptions: {
        adaInputLabel: 'Street Address Search input. Navigate up and down to hear suggested matches after typing in a partial answer'
      }
    };
    autoCompleteUtility.initAutoComplete(fieldId, options);
  }).catch(e => {
    spartaEventsUtility.pub('log-event', {
      messageId: 'google-address-load-API-error',
      messageText: e,
      priority: 'ERROR'
    });
    runFailureCB();
  });
}

/**
 * Initializes the copy for the google address utility. Should be called once on page load.
 * @note - Should not be called once per route, only once per page load.
 * @param {{
 *  dontSeeAddressCopy: String, poweredByGoogleImg: String, poweredByGoogleAlt: String
 * }} copy Copy from JCR
 * @returns {void}
 */
function initCopy({
  dontSeeAddressCopy,
  poweredByGoogleImg,
  poweredByGoogleAlt
}) {
  copy = {
    dontSeeAddressCopy,
    poweredByGoogleImg,
    poweredByGoogleAlt
  };
}
var index = {
  initGoogleAddress,
  initCopy
};

export { index as default };
